import { truncateWithEllipsis } from '@smartfm/container/utils/strings';
import { Button } from '@smartfm/ui/components/Button';
import {
  SearchMenu,
  type SearchMenuProps,
} from '@smartfm/ui/components/SearchMenu';
import pluralize from 'pluralize';
import './styles.scss';

const stickyListItem = {
  // Keep in sync with AdvancedBuildingSelector.__ALL_BUILDINGS_VALUE__
  value: '__ALL_BUILDINGS_VALUE__' as const,
  label: 'All Buildings',
} satisfies SearchMenuProps['itemsMap']['string'];
export interface BuildingSelectorProps
  extends Omit<
    SearchMenuProps,
    'triggerElement' | 'stickyListItemProps' | 'width' | 'itemHeight' | 'source'
  > {
  keyProp: string;
  defaultLabel: string;
  totalCount: number;
  defaultBuilding?: string;
}

function getTriggerLabel(
  values: string[],
  itemsMap: Record<string, SearchMenuProps['itemsMap'][string]>,
  totalCount: number
) {
  if (values.length === 0) {
    return `${stickyListItem.label} (${totalCount})`;
  } else if (values.length === 1) {
    return itemsMap[values[0]]?.label ?? 'Fetching ...';
  }

  return `Multiple buildings (${values.length})`;
}

export function BuildingSelector(props: BuildingSelectorProps) {
  const {
    keyProp,
    values,
    itemsMap,
    totalCount,
    defaultLabel,
    onValueChange,
    favoriteItems,
    defaultBuilding,
    ...otherProps
  } = props;

  const label = getTriggerLabel(values, itemsMap, totalCount);

  function handleAllBuildingsClick() {
    onValueChange(stickyListItem.value);
  }

  // Check if all values are in favoriteItems
  const isAllValuesInFavorites =
    values.length > 0 &&
    values.length <= favoriteItems.length &&
    values.every(value => favoriteItems.includes(value));

  const isDefaultBuilding =  values.length === 1 && values[0] === defaultBuilding;

  return (
    <SearchMenu
      // Due to the way the EmeraldMenu component is implemented,
      // The key is required to force the component to re-render
      // To close the menu each time the value changes
      key={values.join() + keyProp}
      width={448}
      itemHeight={65}
      triggerElement={
        <Button
          icon={isDefaultBuilding ? 'home' : isAllValuesInFavorites ? 'star' : 'place'}
          rightIcon='arrow_drop_down'
          size='Small'
          type='TextButton'
          className='building-selector__trigger'
          title={label}
          label={truncateWithEllipsis(label, 30)}
        />
      }
      stickyListItemProps={{
        isActive: values.length === 0,
        secondaryText: pluralize('Building', totalCount, true),
        onClick: handleAllBuildingsClick,
        ...stickyListItem,
      }}
      values={values}
      itemsMap={itemsMap}
      onValueChange={onValueChange}
      favoriteItems={favoriteItems}
      {...otherProps}
    />
  );
}

BuildingSelector.__ALL_BUILDINGS_VALUE__ = stickyListItem.value;
